<script setup lang="ts">
/**
 * FW-126 — auth yuzeyinin kok bileseni. FW-14'un generik `AppShell` +
 * `FwSurfaceCard`'i (baslik + kart dolgusu) BILEREK burada KULLANILMAZ:
 * tasarim (design/current/Auth v2.dc.html) tam-viewport, kaydirmasiz,
 * iki-kolonlu bir kimlik yuzeyi istiyor — genel "yuzey basligi + kart"
 * kabugu bu tasarimla cakisir. `data-surface="auth"` isareti (FW-113'ten
 * miras, e2e/shell.spec.ts'in dogruladigi) bu kok elemente tasindi.
 *
 * `tema` / `gorsel` (FW-131 — gorunum varyantlari) — design/current/
 * Auth v2.dc.html'in props semasiyle birebir: tema enum('koyu'|'acik'),
 * varsayilan 'koyu'; gorsel boolean, varsayilan true. Console (bu
 * ayarlarin yonetilecegi yuzey) henuz yok; createSurfaceApp kok bilesene
 * prop GECMIYOR (main.ts, createSurfaceApp.ts — grep: 0 eslesme), bu
 * yuzden prop ARAYUZU kalici olsa da varsayilan DEGERLERIN kaynagi
 * literal DEGIL, content/authSurfaceVariant.ts SEED'idir (duzeltme turu
 * 2/2 — bkz. o dosyanin basligi + welcomeContent.ts'teki sol panel
 * icerik seedinin ayni gerekcesi). Console geldiginde tek degisecek yer
 * o SEED dosyasidir; prop arayuzu (test edilebilirlik + ileride
 * Console'dan gecirilebilirlik icin) degismez.
 *
 * `<AuthLegalFooter>` (FW-150) — design/current/Auth v2.dc.html satir
 * 307-310: telif + Gizlilik/Koşullar/Yardım satiri sc-if bloklarinin
 * DISINDA, paylasilan sag panelin ortak parcasidir (onayli artboard'larin
 * TUMUNDE — auth__01..04 — gorunur). FW-145 bunu yalniz EmailView'a
 * baglamisti (o bilet kapsami 01'i asmiyordu, bkz. AuthLegalFooter.vue
 * basligi); burada kabuga tasinarak 02/03/04'te de acik kalan ayni eksik
 * kapatildi. `.fw-auth-shell__form-inner`'in ICINDE kalir (400px form
 * sutunuyla ayni genislik/hiza — tasarimdaki ile birebir).
 *
 * AKIS (FW-175, main'in bulgusu — coklu-boyutlu inceleme): sol panel
 * icerigi eskiden HER ZAMAN `authWelcomeContent` (giris seed'i) basiyordu —
 * `/invite/*` rotalarinda BILE. Onayli `auth__14/15/16.png`'nin UCUNUN DE
 * (`props.akis: "davet"`, durumlar.json) AYRI bir sol panel icerigi
 * ("Nordwind Lojistik çalışma alanına katılın." vb., bkz. content/
 * welcomeContent.ts'in authInviteWelcomeContent basligi) VAR ve hic
 * kurulmamisti. Secim iki kaynaktan:
 *   1. Rota adi — 14 (`invite`) ve 15 (`invite-register`) KESIN bilinir,
 *      peek() daha CEVAP VERMEDEN dogru olur (yanip sonme YOK).
 *   2. `flow.state.invitationToken` — 16-davet-mevcut PasswordView.vue'yu
 *      02-giris-parola ile PAYLASIR (ayni rota, `/password`); akis orada
 *      rota adindan AYIRT EDILEMEZ, bu yuzden InviteView'in "Evet" adiminda
 *      ONCEDEN yazdigi baglam bayragina bakilir (bkz. InviteView.vue).
 *
 * `kayit` akisi (FW-129) — 11/12/13-kayit-*'nin sol paneli (auth__11/12/13.png,
 * `props.akis: "kayit"`). Bu ucu rota adindan KESIN bilinir (`register`,
 * `register-otp`, `register-password`) — davetli akisin 16-davet-mevcut'taki
 * gibi bir "AYNI rota, farkli baglam" belirsizligi YOK, her kayit adiminin
 * KENDI rotasi var.
 *
 * Kiraci adi (`headlineTitleParams`) — davet basliginin {tenant} yer
 * tutucusu (bkz. welcomeContent.ts basligi, "KIRACI ADI DINAMIK" notu);
 * giris basligi bu parametreyi tasimadigi icin zararsizca yoksayilir.
 */
import { computed } from 'vue'
import { useI18n, useRoute } from '@fusapp-work/shell'
import { AuthWelcomePanel, AuthLegalFooter, resolveWelcomeBlocks } from '@fusapp-work/ui-kit'
import { authWelcomeContent, authInviteWelcomeContent, authRegisterWelcomeContent } from './content/welcomeContent'
import { authSurfaceVariant } from './content/authSurfaceVariant'
import { resolveAuthThemeAttribute } from './content/resolveAuthTheme'
import type { AuthThemeName } from './content/resolveAuthTheme'
import { useAuthFlowState } from './composables/useAuthFlowState'

const props = withDefaults(
  defineProps<{
    theme?: AuthThemeName
    showImage?: boolean
  }>(),
  { theme: authSurfaceVariant.theme, showImage: authSurfaceVariant.showImage },
)

const themeAttribute = computed(() => resolveAuthThemeAttribute(props.theme))

const { t } = useI18n()
const route = useRoute()
const flow = useAuthFlowState()
const isInviteRoute = computed(() => route.name === 'invite' || route.name === 'invite-register')
const isRegisterRoute = computed(
  () => route.name === 'register' || route.name === 'register-otp' || route.name === 'register-password',
)
const akis = computed<'giris' | 'davet' | 'kayit'>(() => {
  if (isRegisterRoute.value) return 'kayit'
  if (isInviteRoute.value || flow.state.invitationToken !== null) return 'davet'
  return 'giris'
})
const welcomeContent = computed(() => {
  if (akis.value === 'davet') return authInviteWelcomeContent
  if (akis.value === 'kayit') return authRegisterWelcomeContent
  return authWelcomeContent
})
const headlineTitleParams = computed(() => ({ tenant: flow.state.invitationTenantName ?? '' }))
// FW-224 — tek i18n/provider uygulamada; paket sadece cozulmus sunum verisi alir.
const resolvedWelcomeContent = computed(() => resolveWelcomeBlocks(welcomeContent.value, t, {
  includeImage: props.showImage,
  headlineTitleParams: headlineTitleParams.value,
}))
</script>

<template>
  <div
    class="fw-auth-shell"
    data-surface="auth"
    :data-theme="themeAttribute"
    :data-akis="akis"
  >
    <AuthWelcomePanel
      :content="resolvedWelcomeContent"
      :brand="t('welcome.brand')"
    />
    <main class="fw-auth-shell__form">
      <div class="fw-auth-shell__form-inner">
        <router-view />
        <AuthLegalFooter
          :copyright="t('auth.footer.copyright')"
          :privacy="t('auth.footer.privacy')"
          :terms="t('auth.footer.terms')"
          :help="t('auth.footer.help')"
        />
      </div>
    </main>
  </div>
</template>

<style scoped>
.fw-auth-shell {
  height: 100vh;
  min-height: 640px;
  display: grid;
  grid-template-columns: minmax(0, 46%) minmax(0, 54%);
  background: var(--fw-bg-page);
  color: var(--fw-text-primary);
  overflow: hidden;
}

.fw-auth-shell__form {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: clamp(24px, 4vh, 48px) 48px;
  position: relative;
  min-height: 0;
  overflow: auto;
}

/* design/current/Auth v2.dc.html satir 83 — form sutunu sabit genislikte:
 * kutu ve buton daima ayni genislikte, sol/sag kenarlari hizali. Bu
 * sarmalayici olmadan `.fw-auth-shell__form` satir-yonlu esnek kapta
 * `.step` icerigine gore buzusuyordu (olculdu: 400px yerine 321.20px). */
.fw-auth-shell__form-inner {
  width: 100%;
  max-width: 400px;
  flex: none;
}

@media (max-width: 860px) {
  .fw-auth-shell {
    grid-template-columns: minmax(0, 1fr);
  }

  .fw-auth-shell > :deep(.fw-auth-welcome) {
    display: none;
  }
}
</style>
